import React, { useState } from 'react';
import QRCode from "react-qr-code";
import { Smartphone, Camera, CheckCircle, RotateCcw, ArrowRight, InfoIcon } from 'lucide-react';
import Lottie from "lottie-react"
import animationData from "../../assets/envsetup.json"
import place_laptop from "../../assets/proctoring/laptop.png"
import place_mobile_img from "../../assets/proctoring/mobile.png"
import logger from './../../utils/logger.js';


// Main component for the proctoring setup guide (desktop only)
const ProctoringSetupGuide = ({
  roomUrl,
  onComplete,
  onSendSMS = null,
  isSecondryCameraJoined,
  isProctoringResetup = false,
  initialStep = 0
  // Optional callback for SMS functionality
}) => {
  const [currentStep, setCurrentStep] = useState(initialStep); // 0: QR code, 1: Environment scan, 2: Camera positioning
  const [phoneNumber, setPhoneNumber] = useState('');
  const [isRecording, setIsRecording] = useState(false);
  const [recordingProgress, setRecordingProgress] = useState(0);
  const [environmentScanComplete, setEnvironmentScanComplete] = useState(false);
  const [cameraPositioned, setCameraPositioned] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  // Handle SMS sending
  const handleSendSMS = () => {
    if (onSendSMS && phoneNumber) {
      onSendSMS(phoneNumber);
    }
  };

  // Handle recording of 360° environment scan
  const handleStartRecording = () => {
    setIsRecording(true);

    // Simulate recording progress
    let progress = 0;
    const interval = setInterval(() => {
      progress += 20;
      setRecordingProgress(progress);

      if (progress >= 100) {
        clearInterval(interval);
        setIsRecording(false);
        setEnvironmentScanComplete(true);
      }
    }, 300);
  };

  // Handle camera positioning confirmation
  const handleConfirmPosition = () => {
    setCameraPositioned(true);
  };

  // Handle completion of setup
  const handleCompleteSetup = () => {
    if (onComplete) {
      onComplete();
    }
  };
  

  // Carousel auto-rotation effect
  React.useEffect(() => {
    const interval = setInterval(() => {
      setCurrentImageIndex((prevIndex) => (prevIndex + 1) % 2);
    }, 3000); // Change image every 3 seconds

    return () => clearInterval(interval);
  }, []);

  // Effect to automatically move to the next step when isSecondryCameraJoined becomes true
  React.useEffect(() => {
    // If this is a proctoring resetup, we should stay on step 0 (QR code)
    if (isSecondryCameraJoined && currentStep === 0 && !isProctoringResetup) {
      // Add a small delay before moving to the next step for better UX
      const timer = setTimeout(() => {
        setCurrentStep(1);
      }, 500);
      
      return () => clearTimeout(timer);
    }


    if (isSecondryCameraJoined && currentStep === 0 && isProctoringResetup) {

      logger.debug("Proctoring resetup completed >>>>>>>", isSecondryCameraJoined, currentStep, isProctoringResetup);
      onComplete();
    }
  }, [isSecondryCameraJoined, currentStep, isProctoringResetup]);



  
  // Render different steps
  if (currentStep === 0) {
    return (
      <DesktopEntryScreen
        roomUrl={roomUrl}
        phoneNumber={phoneNumber}
        setPhoneNumber={setPhoneNumber}
        onSendSMS={handleSendSMS}
        onContinue={() => !isProctoringResetup && setCurrentStep(1)}
        isSecondryCameraJoined={isSecondryCameraJoined}
        isProctoringResetup={isProctoringResetup}
      />
    );
  } else if (currentStep === 1) {
    return (
      <DesktopEnvironmentScanStep
        isRecording={isRecording}
        recordingProgress={recordingProgress}
        environmentScanComplete={environmentScanComplete}
        onStartRecording={handleStartRecording}
        onContinue={() => setCurrentStep(2)}
      />
    );
  } else if (currentStep === 2) {
    return (
      <DesktopPositionCameraStep
        cameraPositioned={cameraPositioned}
        onConfirmPosition={handleConfirmPosition}
        onComplete={handleCompleteSetup}
        currentImageIndex={currentImageIndex}
      />
    );
  }

  // Fallback
  return <div>Loading proctoring setup...</div>;
};

// Desktop entry screen with QR code
const DesktopEntryScreen = ({ roomUrl, onContinue, isSecondryCameraJoined, isProctoringResetup = false }) => {
  return (
    <div className="flex flex-col items-center p-6 bg-white rounded-lg max-w-md mx-auto">
      <h2 className="text-2xl font-bold text-gray-800 mb-2">{isProctoringResetup ? 'Reconnect Mobile Proctoring' : 'Set Up Mobile Proctoring'}</h2>
      <p className="text-gray-600 mb-6 text-center">
        {isProctoringResetup 
          ? 'Please reconnect your mobile device for proctoring.' 
          : 'Use your phone to capture your surroundings for a secure interview.'}
      </p>

      <div className="mb-6 flex flex-col items-center">
        <div className="p-3 bg-white rounded-lg shadow-md mb-4">
          <QRCode
            style={{ height: 'auto', maxWidth: "100%", width: "200px" }}
            // value={`https://staging-website.prompthire.in/proctoring?roomUrl=${roomUrl}&step=1`}
            value={`${window.location.origin}/proctoring?roomUrl=${roomUrl}&step=1`}
            // value={`https://a3bb-14-99-245-66.ngrok-free.app/proctoring?roomUrl=${roomUrl}&step=1`}
            viewBox={`0 0 256 256`}
          />
        </div>
        <p className="text-sm text-gray-500 mb-2">Scan the QR code with your phone camera</p>
        <div className="flex items-center justify-center text-blue-600">
          <Smartphone size={20} className="mr-1 font-semibold" />
          <span className="text-sm font-semibold">Scan to begin</span>
        </div>
      </div>

      {/* <div className="w-full border-t border-gray-200 pt-4 mb-4">
        <p className="text-sm text-gray-600 mb-2 text-center">Or send a link to your phone</p>
        <div className="flex">
          <input
            type="tel"
            placeholder="Enter your phone number"
            className="flex-1 px-3 py-2 border border-gray-300 rounded-l-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            value={phoneNumber}
            onChange={(e) => setPhoneNumber(e.target.value)}
          />
          <button 
            className="bg-blue-600 text-white px-4 py-2 rounded-r-md hover:bg-blue-700 transition-colors"
            onClick={onSendSMS}
          >
            Send
          </button>
        </div>
      </div> */}

      {/* Status indicator */}
      <div className="mt-4 w-full">
        {isSecondryCameraJoined ? (
          <div className="flex items-center justify-center p-3 bg-green-50 text-green-700 rounded-md text-sm font-medium">
            <div className="w-3 h-3 rounded-full bg-green-500 mr-2"></div>
            <span>Mobile camera connected! Proceeding to next step...</span>
          </div>
        ) : (
          <div className="flex items-center justify-center p-3 bg-blue-50 text-blue-700 rounded-md text-sm font-medium">
            <div className="w-3 h-3 rounded-full bg-blue-500 animate-pulse mr-2"></div>
            <span>Waiting for mobile camera to connect...</span>
          </div>
        )}
      </div>
    </div>
  );
};

// Desktop environment scan step
const DesktopEnvironmentScanStep = ({
  isRecording,
  recordingProgress,
  environmentScanComplete,
  onStartRecording,
  onContinue
}) => {
  return (
    <div className="flex flex-col items-center p-6 bg-white rounded-lg max-w-md mx-auto">
      <h2 className="text-2xl font-bold text-gray-800 mb-2">Scan Your Environment</h2>
      <p className="text-gray-600 mb-6 text-center">
        Please record a 360° view of your surroundings to verify your environment is secure.
      </p>

      <div className="w-full max-w-sm mb-6">
        {!environmentScanComplete ? (
      <Lottie animationData={animationData} />
        ) : (
          <div className="w-48 h-48 mx-auto bg-green-50 rounded-full flex items-center justify-center mb-4">
            <CheckCircle size={64} className="text-green-500" />
          </div>
        )}
      </div>

      <div className="w-full max-w-sm">
        {!environmentScanComplete ? (
          <button
            className={`w-full py-3 rounded-md text-white font-medium ${isRecording ? 'bg-gray-400 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700'}`}
            onClick={onStartRecording}
            disabled={isRecording}
          >
            {isRecording ? 'Recording...' : 'Start Recording'}
          </button>
        ) : (
          <button
            className="w-full py-3 bg-green-600 hover:bg-green-700 rounded-md text-white font-medium"
            onClick={onContinue}
          >
            Continue to Next Step
          </button>
        )}
      </div>

      {/* <div className="mt-6 text-sm text-gray-500">
        <p>Stand in the center of your room and slowly rotate in a full circle to capture your entire environment.</p>
      </div> */}
    </div>
  );
};

// Desktop camera positioning step
const DesktopPositionCameraStep = ({ cameraPositioned, onConfirmPosition, onComplete, currentImageIndex }) => {
  const [checklistItems, setChecklistItems] = useState({
    faceVisible: false,
    deskVisible: false,
    lightingClear: false
  });

  // Simulate checklist items being checked
  React.useEffect(() => {
    // Simulate face detection after 2 seconds
    const faceTimer = setTimeout(() => {
      setChecklistItems(prev => ({ ...prev, faceVisible: true }));
    }, 1000);

    // Simulate desk detection after 3 seconds
    const deskTimer = setTimeout(() => {
      setChecklistItems(prev => ({ ...prev, deskVisible: true }));
    }, 2000);

    // Simulate lighting check after 4 seconds
    const lightingTimer = setTimeout(() => {
      setChecklistItems(prev => ({ ...prev, lightingClear: true }));
    }, 3000);

    return () => {
      clearTimeout(faceTimer);
      clearTimeout(deskTimer);
      clearTimeout(lightingTimer);
    };
  }, []);

  // Animation state for camera positioning
  const [animationStep, setAnimationStep] = useState(0);
  
  // Progress the animation steps
  React.useEffect(() => {
    if (!cameraPositioned) {
      const animationInterval = setInterval(() => {
        setAnimationStep(prev => (prev + 1) % 4); // Cycle through 4 animation steps
      }, 1500);
      
      return () => clearInterval(animationInterval);
    }
  }, [cameraPositioned]);
  
  const allChecksComplete = checklistItems.faceVisible &&
    checklistItems.deskVisible &&
    checklistItems.lightingClear;

  return (
    <div className="flex flex-col items-center p-6 bg-white rounded-lg max-w-md mx-auto">
      <h2 className="text-2xl font-bold text-gray-800 mb-2">Position Your Camera</h2>
      <p className="text-gray-600 mb-4 text-center">
        Place your camera 3–5 feet away, ensuring it shows both your face and desk clearly.
      </p>

      <div className="w-full max-w-sm mb-6">
        <div className="relative rounded-lg overflow-hidden bg-gray-800 h-64 flex items-center justify-center">
          {/* Carousel for camera positioning illustrations */}
          <div className="relative w-full h-full">
            <img 
              src={currentImageIndex === 0 ? place_laptop : place_mobile_img} 
              alt="Camera Positioning" 
              className="w-full h-full object-contain transition-opacity duration-500" 
            />
          </div>

          {/* Camera framing guidelines */}
          {/* <div className={`absolute inset-0 border-2 ${allChecksComplete ? 'border-green-500 animate-pulse' : 'border-white border-opacity-50'} m-4 pointer-events-none`}></div> */}

          {/* Carousel indicators */}
          <div className="absolute bottom-16 left-0 right-0 flex justify-center space-x-2">
            <div className={`w-2 h-2 rounded-full ${currentImageIndex === 0 ? 'bg-white' : 'bg-white bg-opacity-50'}`}></div>
            <div className={`w-2 h-2 rounded-full ${currentImageIndex === 1 ? 'bg-white' : 'bg-white bg-opacity-50'}`}></div>
          </div>

          {/* Positioning guide */}
          <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-70 text-white p-2 text-sm">
            {/* {allChecksComplete ? (
              <div className="flex items-center justify-center">
                <div className="w-2 h-2 rounded-full bg-green-500 mr-2"></div>
                <span>Perfect position! All requirements met</span>
              </div>
            ) : (
              <div className="flex items-center justify-center">
                <div className="w-2 h-2 rounded-full bg-yellow-500 animate-pulse mr-2"></div>
                <span>Position camera to show your face and desk</span>
              </div>
            )} */}
               <div className="flex items-center justify-center">
                <div className="w-2 h-2 rounded-full bg-yellow-500 animate-pulse mr-2"></div>
                <span>Position camera to show your face and desk</span>
              </div>
          </div>
        </div>
      </div>

      <div className="w-full max-w-sm mb-6">
        <div className="bg-gray-50 rounded-lg p-4">
          <h3 className="text-md font-medium text-gray-700 mb-2">Positioning Checklist</h3>
          <ul>
            <li className="flex items-center mb-2">
              <div className={`w-5 h-5 rounded-full mr-2 flex items-center justify-center ${checklistItems.faceVisible ? 'bg-yellow-500' : 'bg-gray-300'}`}>
                {checklistItems.faceVisible && <InfoIcon size={14} className="text-white" />}
              </div>
              <span className={checklistItems.faceVisible ? 'text-gray-600' : 'text-gray-600'}>
                Face should be visible
              </span>
            </li>
            <li className="flex items-center mb-2">
              <div className={`w-5 h-5 rounded-full mr-2 flex items-center justify-center ${checklistItems.deskVisible ? 'bg-yellow-500' : 'bg-gray-300'}`}>
                {checklistItems.deskVisible && <InfoIcon size={14} className="text-white" />}
              </div>
              <span className={checklistItems.deskVisible ? 'text-gray-600' : 'text-gray-600'}>
                Desk should be visible
              </span>
            </li>
            <li className="flex items-center">
              <div className={`w-5 h-5 rounded-full mr-2 flex items-center justify-center ${checklistItems.lightingClear ? 'bg-yellow-500' : 'bg-gray-300'}`}>
                {checklistItems.lightingClear && <InfoIcon size={14} className="text-white" />}
              </div>
              <span className={checklistItems.lightingClear ? 'text-gray-600' : 'text-gray-600'}>
                Lighting should be clear
              </span>
            </li>
          </ul>
        </div>
      </div>

      <div className="w-full max-w-sm">
        {!cameraPositioned ? (
          <button
            className={`w-full py-3 rounded-md text-white font-medium ${!allChecksComplete ? 'bg-gray-400 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700'}`}
            onClick={onConfirmPosition}
            disabled={!allChecksComplete}
          >
            Confirm Positioning Checklist
          </button>
        ) : (
          <button
            className="w-full py-3 bg-green-600 hover:bg-green-700 rounded-md text-white font-medium"
            onClick={onComplete}
          >
            Start Interview
          </button>
        )}
      </div>
    </div>
  );
};

export default ProctoringSetupGuide;
